import { createContext, useState, useEffect, useContext } from 'react';

const TaskContext = createContext();

// Initial tasks - empty array
const initialTasks = [];

export function TaskProvider({ children }) {
  const [tasks, setTasks] = useState(() => {
    // Try to get tasks from localStorage
    const savedTasks = localStorage.getItem('pomodoro_tasks');
    return savedTasks ? JSON.parse(savedTasks) : initialTasks;
  });
  const [loading, setLoading] = useState(false);

  // Save tasks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pomodoro_tasks', JSON.stringify(tasks));
  }, [tasks]);

  // Add a new task
  const addTask = async (taskData) => {
    const newTask = {
      id: Date.now().toString(),
      title: taskData.title,
      description: taskData.description || '',
      estimated_pomodoros: taskData.estimatedPomodoros,
      completed_pomodoros: 0,
      status: 'not_started',
      priority: taskData.priority || 'medium',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    setTasks(prevTasks => [newTask, ...prevTasks]);
    return newTask;
  };

  // Update a task
  const updateTask = async (taskId, taskData) => {
    setTasks(prevTasks => 
      prevTasks.map(task => {
        if (task.id === taskId) {
          const updatedTask = {
            ...task,
            title: taskData.title !== undefined ? taskData.title : task.title,
            description: taskData.description !== undefined ? taskData.description : task.description,
            estimated_pomodoros: taskData.estimatedPomodoros !== undefined ? 
              taskData.estimatedPomodoros : task.estimated_pomodoros,
            priority: taskData.priority !== undefined ? taskData.priority : task.priority,
            updated_at: new Date().toISOString()
          };
          
          // Update status if provided
          if (taskData.status !== undefined) {
            updatedTask.status = taskData.status;
          }
          
          // Update completed pomodoros if provided
          if (taskData.completedPomodoros !== undefined) {
            updatedTask.completed_pomodoros = taskData.completedPomodoros;
            
            // Auto-complete if all pomodoros are done
            if (updatedTask.completed_pomodoros >= updatedTask.estimated_pomodoros) {
              updatedTask.status = 'completed';
            }
          }
          
          return updatedTask;
        }
        return task;
      })
    );
  };

  // Delete a task
  const deleteTask = async (taskId) => {
    setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
  };

  // Get all tasks
  const getTasks = () => tasks;

  // Get a task by ID
  const getTaskById = (taskId) => tasks.find(task => task.id === taskId);

  // Get active tasks (not completed)
  const getActiveTasks = () => tasks.filter(task => task.status !== 'completed');

  // Get completed tasks
  const getCompletedTasks = () => tasks.filter(task => task.status === 'completed');

  const value = {
    tasks,
    loading,
    addTask,
    updateTask,
    deleteTask,
    getTasks,
    getTaskById,
    getActiveTasks,
    getCompletedTasks,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

export const useTask = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTask must be used within a TaskProvider');
  }
  return context;
};
